import { useCallback, useRef, useState } from 'react';

/**
 * @param {import('../../../lib/floorPlanDesignerState.js').DesignerState} state
 */
function cloneForHistory(state) {
  const copy = JSON.parse(JSON.stringify(state));
  copy.activeChainPointIds = [];
  copy.draftPreviewPoint = null;
  return copy;
}

/**
 * @param {{
 *   present: import('../../../lib/floorPlanDesignerState.js').DesignerState;
 *   onPresentChange: (next: import('../../../lib/floorPlanDesignerState.js').DesignerState) => void;
 *   maxHistory?: number;
 * }} params
 */
export function useFloorPlanHistory({ present, onPresentChange, maxHistory = 10 }) {
  const presentRef = useRef(present);
  presentRef.current = present;

  const onPresentChangeRef = useRef(onPresentChange);
  onPresentChangeRef.current = onPresentChange;

  const undoStackRef = useRef(/** @type {import('../../../lib/floorPlanDesignerState.js').DesignerState[]} */ ([]));
  const redoStackRef = useRef(/** @type {import('../../../lib/floorPlanDesignerState.js').DesignerState[]} */ ([]));
  const transactionSnapshotRef = useRef(
    /** @type {import('../../../lib/floorPlanDesignerState.js').DesignerState | null} */ (null),
  );
  const [revision, setRevision] = useState(0);

  const bumpIfNeeded = useCallback((shouldBump) => {
    if (shouldBump) {
      setRevision((value) => value + 1);
    }
  }, []);

  const commit = useCallback(
    (next) => {
      undoStackRef.current = [
        ...undoStackRef.current.slice(-(maxHistory - 1)),
        cloneForHistory(presentRef.current),
      ];
      redoStackRef.current = [];
      transactionSnapshotRef.current = null;
      onPresentChangeRef.current(next);
      setRevision((value) => value + 1);
    },
    [maxHistory],
  );

  const apply = useCallback((next) => {
    onPresentChangeRef.current(next);
  }, []);

  const beginTransaction = useCallback(() => {
    if (!transactionSnapshotRef.current) {
      transactionSnapshotRef.current = cloneForHistory(presentRef.current);
    }
  }, []);

  const endTransaction = useCallback(
    (next) => {
      let didPush = false;
      if (transactionSnapshotRef.current) {
        undoStackRef.current = [
          ...undoStackRef.current.slice(-(maxHistory - 1)),
          transactionSnapshotRef.current,
        ];
        redoStackRef.current = [];
        transactionSnapshotRef.current = null;
        didPush = true;
      }
      onPresentChangeRef.current(next);
      bumpIfNeeded(didPush);
    },
    [maxHistory, bumpIfNeeded],
  );

  const undo = useCallback(() => {
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    redoStackRef.current = [...redoStackRef.current, cloneForHistory(presentRef.current)];
    transactionSnapshotRef.current = null;
    onPresentChangeRef.current(previous);
    setRevision((value) => value + 1);
  }, []);

  const redo = useCallback(() => {
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current = [...undoStackRef.current, cloneForHistory(presentRef.current)];
    transactionSnapshotRef.current = null;
    onPresentChangeRef.current(next);
    setRevision((value) => value + 1);
  }, []);

  const clearHistory = useCallback(() => {
    const hadHistory =
      undoStackRef.current.length > 0 ||
      redoStackRef.current.length > 0 ||
      transactionSnapshotRef.current !== null;
    undoStackRef.current = [];
    redoStackRef.current = [];
    transactionSnapshotRef.current = null;
    bumpIfNeeded(hadHistory);
  }, [bumpIfNeeded]);

  void revision;

  return {
    commit,
    apply,
    beginTransaction,
    endTransaction,
    undo,
    redo,
    canUndo: undoStackRef.current.length > 0,
    canRedo: redoStackRef.current.length > 0,
    clearHistory,
  };
}

export default useFloorPlanHistory;
